import type { IntlShape } from 'react-intl'

import type { EmployeeStore } from 'Stores/employeeStore/employeeStore'
import type { OrderStore } from 'Stores/orderStore/orderStore'
import type { OrderAddressFields } from 'Stores/orderStore/orderStore.types'
import type { OrganisationStore } from 'Stores/organisationStore/organisationStore'
import type { PortalSettingsStore } from 'Stores/portalSettingsStore/portalSettingsStore'
import { UserModeEnum } from 'Stores/portalSettingsStore/portalSettingsStore.types'
import type { RentalStore } from 'Stores/rentalStore/rentalStore'
import type { UserStore } from 'Stores/userStore/userStore'

import {
  formatOffersShippingAddressDataBeforeSubmit,
  formatShippingAddressDataBeforeSubmit,
} from 'Utilities'

import type { mutationCreateOfferOrdersVariables } from 'Constants/graphql/mutations/__generated__/mutationCreateOfferOrders'
import type { mutationEarlyUpgradeDeviceVariables } from 'Constants/graphql/mutations/__generated__/mutationEarlyUpgradeDevice'
import type { mutationNewDeviceCaseItemVariables } from 'Constants/graphql/mutations/__generated__/mutationNewDeviceCaseItem'
import type { mutationUpgradeDeviceVariables } from 'Constants/graphql/mutations/__generated__/mutationUpgradeDevice'
import type {
  getOrganisationConfigurationList_getOrganisationConfigurationList,
  getOrganisationConfigurationList_getOrganisationConfigurationList_attributes,
} from 'Constants/graphql/queries/organisation/__generated__/getOrganisationConfigurationList'
import { ORGANISATION_CONFIG_LIST_EXTERNAL_CASE_ID } from 'Constants/organisationConfig'

import { OrderAddressType, OrderType } from 'Portal/__generated__/globalTypes'
import { productOffersDeviceItemPayloadMapper } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.mapper'
import { ShippingAddressTypeEnum } from 'Portal/src/types/address/address.types'

interface getOrderMutationVariablesParams {
  orderType: OrderType
  chosenAccessories: OrderStore['chosenAccessories']
  chosenDevice: OrderStore['chosenDevice']
  activeEmployee: EmployeeStore['employee'] | null
  activeOrganisation: OrganisationStore['organisation'] | null
  chosenShippingAddress: OrderStore['chosenShippingAddress']
  user: UserStore['user']
  activeEmployeeId?: string
  officeAddress?: OrderAddressFields | null
  rentalDetails?: RentalStore['rentalDetails']
  orderExternalId?: OrderStore['orderExternalId']
}

export interface getRentCategoryOrderMutationVariablesParams
  extends getOrderMutationVariablesParams {
  userMode?: PortalSettingsStore['userMode']
}

export const getRentCategoryOrderMutationVariables = (
  params: getRentCategoryOrderMutationVariablesParams,
):
  | mutationNewDeviceCaseItemVariables
  | mutationEarlyUpgradeDeviceVariables
  | mutationUpgradeDeviceVariables => {
  const {
    orderType,
    chosenAccessories,
    chosenDevice,
    activeEmployee,
    activeOrganisation,
    chosenShippingAddress,
    activeEmployeeId,
    user,
    userMode,
    officeAddress,
    rentalDetails,
    orderExternalId,
  } = params

  const shippingAddressLabel =
    userMode === UserModeEnum.NEW_JOINER
      ? 'New joiner'
      : chosenShippingAddress?._label

  const isEmployeeAndUsingOfficeAddress =
    !!activeEmployee && chosenShippingAddress?._label === officeAddress?.name

  return {
    input: {
      accessories:
        chosenAccessories.size > 0
          ? [...chosenAccessories].map(([, accessory]) => ({
              billingIdentifier: accessory?.billingIdentifier,
              customClientReference: accessory?.customClientReference,
              customerLabel: accessory?.customerLabel ?? '',
              id: accessory.deviceDefinition.id,
              poRcn: accessory?.poRcn,
              quantity: accessory.quantity,
            }))
          : null,
      addressID: isEmployeeAndUsingOfficeAddress
        ? officeAddress?.id
        : undefined,
      device: {
        billingIdentifier: chosenDevice?.billingIdentifier,
        customClientReference: chosenDevice?.customClientReference,
        customerLabel: chosenDevice?.customerLabel ?? '',
        id: chosenDevice?.deviceDefinition.id ?? '',
        poRcn: chosenDevice?.poRcn,
        quantity: chosenDevice?.quantity ?? 1,
      },
      employeeID: activeEmployeeId,
      externalID: orderExternalId || undefined,
      isRepairUpgrade:
        orderType === OrderType.newDevice
          ? undefined
          : orderType === OrderType.repairUpgrade,
      organisationID:
        activeEmployee?.organisation?.id.toString() ||
        activeOrganisation?.id.toString() ||
        '',
      previousRentalID:
        orderType === OrderType.newDevice
          ? undefined
          : (rentalDetails?.rental.id ?? ''),
      previousStocklistItemID:
        orderType === OrderType.newDevice
          ? undefined
          : rentalDetails?.rental.stocklistItem?.id,
      rentCategoryID: chosenDevice?.rentCategoryId || '',
      requestingUserID: user.userId?.toString() || '',
      shippingAddress: {
        ...formatShippingAddressDataBeforeSubmit(
          chosenShippingAddress,
          isEmployeeAndUsingOfficeAddress,
        )!,
        _label:
          orderType === OrderType.newDevice ? shippingAddressLabel : undefined,
      },
      vendingMachineID:
        orderType === OrderType.newDevice
          ? chosenShippingAddress?._vendingMachineId
          : undefined,
      ...(chosenShippingAddress?.privateEmailAddress && {
        privateEmailAddress: chosenShippingAddress.privateEmailAddress,
      }),
    },
  }
}

export interface getOffersOrderMutationVariablesParams
  extends getOrderMutationVariablesParams {
  phonePlanDetails?: OrderStore['phonePlanDetails']
}

export const getOfferOrderMutationVariables = (
  params: getOffersOrderMutationVariablesParams,
): mutationCreateOfferOrdersVariables => {
  const {
    orderType,
    chosenAccessories,
    chosenDevice,
    activeEmployee,
    activeOrganisation,
    chosenShippingAddress,
    activeEmployeeId,
    user,
    officeAddress,
    rentalDetails,
    orderExternalId,
    phonePlanDetails,
  } = params

  const isEmployeeAndUsingOfficeAddress =
    !!activeEmployee && chosenShippingAddress?._label === officeAddress?.name

  const isHandover =
    chosenShippingAddress?.addressType === ShippingAddressTypeEnum.HANDOVER

  const handoverEmptyAddress = {
    city: '',
    country: '',
    label: chosenShippingAddress?._label || '',
    name: '',
    nameAddition: '',
    street: '',
    streetAddition: '',
    type: OrderAddressType.handover,
    zipcode: '',
  }

  return {
    input: {
      orders: [
        {
          addressID: isEmployeeAndUsingOfficeAddress
            ? officeAddress?.id
            : undefined,
          employeeId: activeEmployeeId,
          externalID: orderExternalId || undefined,
          items: productOffersDeviceItemPayloadMapper(
            chosenDevice,
            chosenAccessories.size > 0
              ? [...chosenAccessories]
                  .map(([_id, accessory]) => accessory)
                  .filter(accessory =>
                    orderType === OrderType.newDevice
                      ? accessory.quantity > 0
                      : true,
                  )
              : null,
          ),
          offerId: chosenDevice?.offerId || '',
          orderType,
          organisationId:
            activeEmployee?.organisation?.id.toString() ||
            activeOrganisation?.id.toString() ||
            '',
          phonePlanDetails: phonePlanDetails || undefined,
          previousRentalID:
            orderType === OrderType.newDevice
              ? undefined
              : rentalDetails?.rental.id,
          primaryDeviceImei: chosenShippingAddress?.imei1 || undefined,
          shippingAddress: isHandover
            ? handoverEmptyAddress
            : formatOffersShippingAddressDataBeforeSubmit(
                chosenShippingAddress,
                OrderAddressType.shipping,
                isEmployeeAndUsingOfficeAddress,
              ),
          vendingMachineID:
            chosenShippingAddress?._vendingMachineId || undefined,
        },
      ],
      requestingUserID: user.userId?.toString() ?? '0',
    },
  }
}

const orderTypeToAttributeMap = {
  [OrderType.lostDevice]: 'lost-device',
  [OrderType.newDevice]: 'new-device',
  [OrderType.repairUpgrade]: 'repair-device',
  [OrderType.upgrade]: 'upgrade-device',
  [OrderType.earlyUpgrade]: 'upgrade-device',

  lostStolen: 'lost-device',
  // Legacy modals types
  replaceBroken: 'repair-device',
}

export const getOrderAttribute = (
  organisationConfigList:
    | getOrganisationConfigurationList_getOrganisationConfigurationList[]
    | null,
  type: keyof typeof orderTypeToAttributeMap,
): getOrganisationConfigurationList_getOrganisationConfigurationList_attributes | null => {
  if (!organisationConfigList) {
    return null
  }

  return (
    organisationConfigList
      ?.find(({ name }) => name === ORGANISATION_CONFIG_LIST_EXTERNAL_CASE_ID)
      ?.attributes?.find(
        attribute => attribute.attributeName === orderTypeToAttributeMap[type],
      ) || null
  )
}

export const getExternalIdCustomName = (
  organisationConfigList:
    | getOrganisationConfigurationList_getOrganisationConfigurationList[]
    | null,
  intl: IntlShape,
): string =>
  organisationConfigList
    ?.find(({ name }) => name === ORGANISATION_CONFIG_LIST_EXTERNAL_CASE_ID)
    ?.attributes?.find(attribute => attribute.attributeName === 'custom-text')
    ?.attributeValue ||
  intl.formatMessage({
    id: 'NewOrderSummary_external_id',
  })
